import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import {
  EMPTY_WAITLIST_APPLICATION,
  NO_WAITLIST_ENTRY,
  validateWaitlistApplication,
  waitlistOffered,
} from '../../_helpers/countryEligibility.js';
import '../../styles/partial/MusicianWaitlist.css';

const classes = (...names) => names.filter(Boolean).join(' ');

const TITLE_ID = 'musician-waitlist-title';
const HINT_ID = 'musician-waitlist-hint';
const ERROR_ID = 'musician-waitlist-error';

/**
 * The application's inputs, in order. `required` is the backend's own rule
 * (only the artist name is); `multiline` gets a textarea.
 */
const FIELDS = Object.freeze([
  { name: 'artistName', type: 'text', required: true, autoComplete: 'organization' },
  { name: 'genre', type: 'text' },
  { name: 'portfolioUrl', type: 'url', autoComplete: 'url' },
  { name: 'message', multiline: true },
]);

const fieldId = (name) => `musician-waitlist-${name}`;

/**
 * What the register-performer page shows where performer sign-up is not open
 * (ENG-375). Two closed states, told apart because they promise different
 * things:
 *
 * - `waitlist` — we mean to open here. The card says so, names the country
 *   when the backend could place the account ("your country" when it could
 *   not, rather than a raw `GB`), and offers the one useful action: a short
 *   application (ENG-476) — the artist name the backend requires, and the
 *   genre, a link and a note it takes if given. There is no address to type:
 *   we write to the one on the account, from the SSO sign-in.
 * - `unsupported` — we cannot onboard performers here and no date is
 *   promised, so there is no join button. Offering one would collect an
 *   application against a mail nobody intends to send. The card asks
 *   `waitlistOffered` rather than naming the state, so that rule lives in
 *   `countryEligibility.js` alone.
 *
 * `unsupported` wins over an entry from an earlier `waitlist` state, so a
 * country that has since closed does not keep telling a musician we will
 * write when it opens. Both states keep the way back to the listener side of
 * the app, which works in every country SongShake runs in.
 *
 * The three faces are one element tree, not three: heading, then the line
 * that says where things stand, then whatever that state offers. The join
 * swaps the first two rather than replacing the card, which is what lets the
 * middle line be a live region that was already there (see below).
 *
 * The card holds only what is being typed and what it found wrong with it;
 * the join itself, its failure and the entry live in `useCountryEligibility`,
 * so a retry is the same call again rather than a second source of truth. A
 * failure names the field it is about when there is one — the card's own
 * check, or the backend's 400 — and only that field is marked invalid. Like
 * `LanguageSelector`, it carries no visual treatment of its own: the host
 * page passes its form classes in through `classNames`, so the card looks
 * like the form it replaces without this sheet re-specifying it.
 */
function MusicianWaitlist({
  country,
  eligibility,
  waitlist = NO_WAITLIST_ENTRY,
  joining = false,
  joinError = null,
  onJoin,
  classNames = {},
}) {
  const { t, format } = useLocale();
  const navigate = useNavigate();
  const [application, setApplication] = useState(EMPTY_WAITLIST_APPLICATION);
  const [formError, setFormError] = useState(null);
  const headingRef = useRef(null);

  const offersWaitlist = waitlistOffered(eligibility);
  const joined = offersWaitlist && waitlist.joined;
  // Whether the card has *been through* the join, as against having found the
  // account on the list when it mounted; only the first is a transition.
  const wasJoined = useRef(joined);

  useEffect(() => {
    // The join replaces the form — the submit button that was pressed
    // included — with the confirmation. Nothing else moves focus, so it would
    // fall to <body>: the keyboard user loses their place on the page and a
    // screen reader is left mid-nowhere. It goes to the heading of what took
    // the form's place, which is also the surest announcement there is.
    if (joined && !wasJoined.current) headingRef.current?.focus();
    wasJoined.current = joined;
  }, [joined]);

  // The country in the reader's language; the backend's two-letter code is
  // not copy, so an account it could not place says "your country" instead.
  const countryName = format.country(country) || t('registerBand.waitlist.yourCountry');
  // A hook failure is `{ key }` | `{ text }` (ENG-380): the catalog message
  // follows a language switch, the backend's own wording is shown as sent.
  const error = formError ?? joinError;
  const joinedAt = joined ? format.date(waitlist.joinedAt, 'short') : '';

  const titleKey = joined
    ? 'registerBand.waitlist.joinedTitle'
    : offersWaitlist
      ? 'registerBand.waitlist.title'
      : 'registerBand.waitlist.unsupportedTitle';

  // Where things stand, in one line. The headings name the country too, so a
  // German musician does not read "in your country" above "in Germany".
  const standing = joined
    ? t('registerBand.waitlist.joinedDescription', { country: countryName })
    : offersWaitlist
      ? t('registerBand.waitlist.description', { country: countryName })
      : t('registerBand.waitlist.unsupportedDescription', { country: countryName });

  const handleSubmit = (event) => {
    event.preventDefault();
    // The button is `aria-disabled` rather than `disabled` (see below), so a
    // second press while the first is out has to be turned away here.
    if (joining) return;
    const problem = validateWaitlistApplication(application);
    if (problem) {
      setFormError(problem);
      document.getElementById(fieldId(problem.field))?.focus();
      return;
    }
    setFormError(null);
    onJoin(application);
  };

  const handleChange = (name) => (event) => {
    const { value } = event.target;
    setApplication((current) => ({ ...current, [name]: value }));
    // Typing answers the complaint about this field, so it goes.
    if (formError?.field === name) setFormError(null);
  };

  const findPerformers = (
    <button
      type="button"
      className={classes('musician-waitlist-secondary', classNames.secondary)}
      onClick={() => navigate('/performers')}
    >
      {t('common.actions.findPerformers')}
    </button>
  );

  const renderField = ({ name, type, required, multiline, autoComplete }) => {
    const id = fieldId(name);
    // Invalid only when the value is what is wrong: a join the backend
    // refused for its own reasons is reported above the form without calling
    // anything typed into it wrong.
    const invalid = error?.field === name;
    const control = {
      id,
      name,
      value: application[name],
      onChange: handleChange(name),
      placeholder: t(`registerBand.waitlist.${name}Placeholder`),
      'aria-required': required ? 'true' : undefined,
      'aria-invalid': invalid ? 'true' : undefined,
      // The complaint is read with the field it is about, not only when the
      // alert fires and the musician has moved on.
      'aria-describedby': invalid ? ERROR_ID : undefined,
    };
    return (
      <div className="musician-waitlist-field" key={name}>
        <label className={classes('musician-waitlist-label', classNames.label)} htmlFor={id}>
          {t(`registerBand.waitlist.${name}Label`)}
          {required && (
            <>
              {' '}
              {/* Marked the way the host form marks its own required fields,
                  rather than only complaining after the press. */}
              <span className={classes('musician-waitlist-required', classNames.required)} aria-hidden="true">*</span>
            </>
          )}
        </label>
        {multiline ? (
          <textarea {...control} rows={4} className={classes('musician-waitlist-input', classNames.textarea ?? classNames.input)} />
        ) : (
          <input {...control} type={type} autoComplete={autoComplete} className={classes('musician-waitlist-input', classNames.input)} />
        )}
      </div>
    );
  };

  return (
    <section className={classes('musician-waitlist', classNames.root)} aria-labelledby={TITLE_ID} data-testid="musician-waitlist">
      {/* tabIndex={-1} so the join can hand focus here; it stays out of the
          tab order, where a heading does not belong. */}
      <h2
        className={classes('musician-waitlist-title', classNames.title)}
        id={TITLE_ID}
        ref={headingRef}
        tabIndex={-1}
      >
        {t(titleKey, { country: countryName })}
      </h2>

      {/* The one line that changes when the join lands, and a live region
          from the first render rather than one inserted already full of text
          — several screen readers only announce a region that was there
          before its content changed. It is the visible copy as well, so the
          confirmation is not also duplicated into the accessibility tree. */}
      <p className="musician-waitlist-text" role="status">{standing}</p>

      {joined ? (
        <>
          {/* Only when the date parsed: a stamp the backend left off, or wrote
              in a shape Date cannot read, must not print an empty "Added ". */}
          {joinedAt && <p className="musician-waitlist-meta">{t('registerBand.waitlist.joinedOn', { date: joinedAt })}</p>}
          <div className="musician-waitlist-actions">{findPerformers}</div>
        </>
      ) : !offersWaitlist ? (
        <>
          <p className="musician-waitlist-text">{t('registerBand.waitlist.listenersWelcome')}</p>
          <div className="musician-waitlist-actions">{findPerformers}</div>
        </>
      ) : (
        <form className="musician-waitlist-form" onSubmit={handleSubmit} aria-describedby={HINT_ID} noValidate>
          {error && (
            <div className={classes('musician-waitlist-error', classNames.error)} id={ERROR_ID} role="alert">
              {error.key ? t(error.key) : error.text}
            </div>
          )}

          {FIELDS.map(renderField)}

          <p className="musician-waitlist-hint" id={HINT_ID}>
            {t('registerBand.waitlist.accountEmailHint')}
          </p>

          <div className="musician-waitlist-actions">
            {findPerformers}
            {/* aria-disabled rather than disabled: a button that goes inert
                while the request is out would drop keyboard focus to <body>. */}
            <button
              type="submit"
              className={classes('musician-waitlist-primary', classNames.primary)}
              aria-disabled={joining || undefined}
            >
              {joining ? t('registerBand.waitlist.actions.joining') : t('registerBand.waitlist.actions.join')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export default MusicianWaitlist;
